import { basicInfoAtom, cartAtom, deliveryServiceAtom } from "@/atoms"
import { tableAtom } from "@/atoms/table"
import deliveryAreaConfigAtom from "@/atoms/delivery-area-config"
import { Address } from "@/types"
import { useAtomValue } from "jotai"
import { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { getAddressServiceAreaStatus, isUsableServiceArea } from "../components/service-area"
import { useStorefrontScheduling } from "@/hooks/use-storefront-scheduling"
import {
  CheckoutStep,
  STEP_INDEX,
  checkoutAtom,
  firstInvalidStep,
  isScheduleStepVisible,
} from "../state"
import { useDiscountPlan } from "./useDiscountPlan"

/**
 * Sends the customer back to the first step that is not done yet. While the
 * discounts are still being evaluated the total is unknown, so the cash
 * change check waits for it instead of bouncing on a total without coupons.
 */
export function useEnforceWizardEntry(currentStep: CheckoutStep, addresses: Address[]): void {
  const state = useAtomValue(checkoutAtom)
  const cart = useAtomValue(cartAtom)
  const basicInfo = useAtomValue(basicInfoAtom)
  const deliveryService = useAtomValue(deliveryServiceAtom)
  const deliveryAreaConfig = useAtomValue(deliveryAreaConfigAtom)
  const tableCtx = useAtomValue(tableAtom)
  const scheduling = useStorefrontScheduling()
  const navigate = useNavigate()
  const plan = useDiscountPlan()
  const evaluating = plan.coupons.phase === "checking" || plan.rulePromotions.phase === "checking"
  const orderTotal = evaluating ? 0 : plan.pricing.total
  const scheduleStepVisible = isScheduleStepVisible(
    state,
    scheduling.schedulableChannels.map((channel) => channel.serviceType),
  )

  useEffect(() => {
    if (tableCtx) {
      if (currentStep !== "review") {
        navigate("/cart/checkout/review", { replace: true })
      }
      return
    }

    const serviceArea = deliveryService?.serviceArea
    const merchantAddress = basicInfo?.address

    const hasDeliverableAddress = (addressId: string) => {
      if (!isUsableServiceArea(serviceArea, deliveryAreaConfig)) return false
      const address = addresses.find((a) => a.id === addressId)
      if (!address) return false
      const { availablePolygons } = getAddressServiceAreaStatus(
        address,
        serviceArea,
        merchantAddress,
        deliveryAreaConfig,
      )
      return availablePolygons.length > 0
    }

    const blocking = firstInvalidStep({
      state,
      cartTotal: cart.totalPrice,
      orderTotal,
      hasDeliverableAddress,
      scheduleStepVisible,
    })

    if (!blocking) return
    if (STEP_INDEX[blocking] < STEP_INDEX[currentStep]) {
      navigate(`/cart/checkout/${blocking}`, { replace: true })
    }
  }, [
    state,
    cart,
    basicInfo,
    deliveryService,
    deliveryAreaConfig,
    addresses,
    currentStep,
    navigate,
    tableCtx,
    scheduleStepVisible,
    orderTotal,
  ])
}
